/**
 * MeMark Smart URL & Monogram Intelligence Engine
 */

const TRACKING_PARAMS = [
  'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'utm_id',
  'fbclid', 'gclid', 'dclid', 'msclkid', 'yclid', 'zanpid',
  'ref', 'ref_src', 'ref_url', 'source', 'si', 'mc_cid', 'mc_eid',
  '_hsenc', '_hsmi', 'mkt_tok', 'igshid',
];

/**
 * Strips tracking parameters from a URL while preserving core query data.
 */
export function cleanTrackingParams(rawUrl) {
  if (!rawUrl || typeof rawUrl !== 'string') return rawUrl;
  try {
    const url = new URL(rawUrl.trim());
    TRACKING_PARAMS.forEach((param) => {
      url.searchParams.delete(param);
    });
    // Remove empty hash if left behind
    let cleaned = url.toString();
    if (cleaned.endsWith('#') || cleaned.endsWith('?')) {
      cleaned = cleaned.slice(0, -1);
    }
    return cleaned;
  } catch {
    return rawUrl.trim();
  }
}

/**
 * Extracts a clean domain string (e.g. "github.com") from a URL.
 */
export function extractDomain(rawUrl) {
  if (!rawUrl || typeof rawUrl !== 'string') return '';
  try {
    return new URL(rawUrl).hostname.replace(/^www\./, '');
  } catch {
    return rawUrl.replace(/^https?:\/\//, '').replace(/^www\./, '').split('/')[0];
  }
}

/**
 * Deterministic color palette & 2-letter initials generator for monograms.
 */
const MONOGRAM_PALETTES = [
  { bg: 'from-emerald-500/20 to-teal-700/30', border: 'border-emerald-500/30', text: '#34d399' },
  { bg: 'from-cyan-500/20 to-blue-700/30', border: 'border-cyan-500/30', text: '#22d3ee' },
  { bg: 'from-violet-500/20 to-purple-700/30', border: 'border-violet-500/30', text: '#a78bfa' },
  { bg: 'from-amber-500/20 to-orange-700/30', border: 'border-amber-500/30', text: '#fbbf24' },
  { bg: 'from-rose-500/20 to-pink-700/30', border: 'border-rose-500/30', text: '#fb7185' },
  { bg: 'from-indigo-500/20 to-sky-700/30', border: 'border-indigo-500/30', text: '#818cf8' },
];

export function getMonogramData(title = '', url = '') {
  const seed = (title || url || 'Bookmark').trim();
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    hash = (hash << 5) - hash + seed.charCodeAt(i);
    hash |= 0;
  }
  const palette = MONOGRAM_PALETTES[Math.abs(hash) % MONOGRAM_PALETTES.length];

  // Extract 2 initials
  const parts = seed.split(/[\s\-._]+/).filter(Boolean);
  let initials = 'BM';
  if (parts.length >= 2) {
    initials = (parts[0][0] + parts[1][0]).toUpperCase();
  } else if (parts.length === 1 && parts[0].length >= 2) {
    initials = parts[0].slice(0, 2).toUpperCase();
  } else if (parts.length === 1) {
    initials = parts[0][0].toUpperCase();
  }

  return { initials, ...palette };
}

/**
 * Returns Wayback Machine snapshot lookup URL for a dead/broken link.
 */
export function getWaybackUrl(rawUrl) {
  if (!rawUrl) return '';
  return `https://web.archive.org/web/*/${encodeURIComponent(rawUrl)}`;
}

/**
 * Parses bulk URLs from plaintext, Markdown links [Title](URL), or raw pastes.
 */
export function parseBulkUrls(rawText = '') {
  if (!rawText || !rawText.trim()) return [];
  const lines = rawText.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  const results = [];

  const mdRegex = /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/i;
  const urlRegex = /(https?:\/\/[^\s]+)/i;

  for (const line of lines) {
    const mdMatch = line.match(mdRegex);
    if (mdMatch) {
      results.push({
        title: mdMatch[1].trim(),
        url: cleanTrackingParams(mdMatch[2].trim()),
      });
      continue;
    }

    const urlMatch = line.match(urlRegex);
    if (urlMatch) {
      const u = cleanTrackingParams(urlMatch[1].trim());
      const dom = extractDomain(u);
      // Derive a nice readable title if none given
      const name = dom ? dom.split('.')[0] : 'Bookmark';
      const title = line.replace(urlMatch[1], '').replace(/[-–—:|]/g, '').trim() ||
        (name.charAt(0).toUpperCase() + name.slice(1));
      results.push({ title, url: u });
    }
  }

  return results;
}
